import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, ArrowRight, Shield, Zap, Users, DollarSign, Calendar, CreditCard, Clock, Smartphone, TrendingUp, Video } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { useFoundersPromoCount } from '../../hooks/useFoundersPromoCount';
import { getFoundersPromoDisplay } from '../../utils/foundersPromoDisplay';
import { GOOGLE_CALENDAR_BOOKING_URL } from '../../constants/appStore';
import { TwinklingGoldBorder } from '../../components/ui/TwinklingGoldBorder';
import { supabase } from '../../lib/supabase';
import { devLog } from '../../utils/devLog';

// Video configuration - upload videos to promo-videos bucket with this filename
const PROMO_VIDEO_FILENAME = 'founders-promo.mp4';

// Tracking functions for pixels/analytics
function trackFoundersPromoPageVisit() {
  try {
    // Store visit in localStorage
    const trackingData = {
      page: 'founders-promo',
      timestamp: new Date().toISOString(),
      visited: true,
    };
    localStorage.setItem('rollops_founders_promo_visit', JSON.stringify(trackingData));

    // Track with Google Analytics if available
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof window !== 'undefined' && (window as any).gtag) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).gtag('event', 'founders_promo_page_visit', {
        page: 'founders-promo',
        timestamp: trackingData.timestamp,
      });
    }

    // Apollo tracking removed - will be replaced with proper analytics later
  } catch (error) {
    // Error tracking page visit - silently fail
  }
}

function trackFoundersPromoCTAClick(ctaLocation: string) {
  try {
    // Track CTA click
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof window !== 'undefined' && (window as any).gtag) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).gtag('event', 'founders_promo_cta_click', {
        cta_location: ctaLocation,
        timestamp: new Date().toISOString(),
      });
    }

    // Apollo tracking removed - will be replaced with proper analytics later
  } catch (error) {
    // Error tracking CTA click - silently fail
  }
}

function trackFoundersPromoAbandon() {
  try {
    const existingData = localStorage.getItem('rollops_founders_promo_visit');
    if (!existingData) return;

    const trackingData = JSON.parse(existingData);
    
    // Track abandonment
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (typeof window !== 'undefined' && (window as any).gtag) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).gtag('event', 'founders_promo_abandon', {
        page: 'founders-promo',
        visited_at: trackingData.timestamp,
        abandoned_at: new Date().toISOString(),
      });
    }

    // Apollo tracking removed - will be replaced with proper analytics later
  } catch (error) {
    // Error tracking abandon - silently fail
  }
}

// Transition Calculator Component
function TransitionCalculator() {
  const [inputs, setInputs] = useState({
    currentSaaS: 100,
    currentProcessingFee: 4,
    memberCount: 50,
    avgMembershipPrice: 100,
    monthlyGrowthRate: 2,
    retentionRate: 85,
  });

  // Calculate monthly revenue
  const monthlyRevenue = inputs.memberCount * inputs.avgMembershipPrice;
  
  // Current costs
  const currentSaaSCost = inputs.currentSaaS;
  const currentProcessingCost = monthlyRevenue * (inputs.currentProcessingFee / 100);
  const currentTotalCost = currentSaaSCost + currentProcessingCost;
  
  // RollOps costs
  const rollOpsSaaSCost = 20;
  
  // Calculate transition over 12 months
  const calculateTransition = () => {
    const months = [];
    let members = inputs.memberCount;
    let cumulativeSavings = 0;
    
    for (let month = 1; month <= 12; month++) {
      // Member growth
      members = Math.round(members * (1 + inputs.monthlyGrowthRate / 100));
      
      // Transition percentage (gradual increase)
      const transitionPercent = Math.min(month * 8.33, 100); // Full transition by month 12
      
      // Calculate costs
      const membersOnZeroFee = Math.round(members * (transitionPercent / 100));
      const membersOnOldFee = members - membersOnZeroFee;
      
      // Processing fees: old members pay old rate, new members pay 0
      const processingCost = (membersOnOldFee * inputs.avgMembershipPrice) * (inputs.currentProcessingFee / 100);
      const rollOpsTotalCost = rollOpsSaaSCost + processingCost;
      
      // Savings this month
      const monthlySavings = currentTotalCost - rollOpsTotalCost;
      cumulativeSavings += monthlySavings;
      
      months.push({
        month,
        members,
        membersOnZeroFee,
        membersOnOldFee,
        rollOpsTotalCost,
        monthlySavings,
        cumulativeSavings,
      });
    }
    
    return months;
  };

  const transitionData = calculateTransition();
  const immediateSavings = currentSaaSCost - rollOpsSaaSCost;
  const year1TotalSavings = transitionData[11].cumulativeSavings;
  const month12Savings = transitionData[11].monthlySavings;
  const projectedMembers = transitionData[11].members;

  return (
    <div className="bg-[#1A1818] rounded-lg p-6 mb-6">
      <div className="flex items-center gap-3 mb-6">
        <TrendingUp className="w-6 h-6 text-rollops-accent" />
        <h3 className="text-xl font-bold">Transition Plan Calculator</h3>
      </div>
      <p className="text-sm text-[#A9A3A3] mb-6">
        Enter your current numbers to see estimated savings and growth projections. All numbers are estimates based on average industry metrics.
      </p>

      {/* Input Fields */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Current SaaS Cost ($/month)
          </label>
          <input
            type="number"
            value={inputs.currentSaaS}
            onChange={(e) => setInputs({ ...inputs, currentSaaS: parseFloat(e.target.value) || 0 })}
            className="w-full px-4 py-2 rounded-lg bg-[#111010] text-[#EBFFEE] border border-[#504A4A] focus:border-[#9B1421] focus:outline-none"
            min="0"
            step="1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Current Processing Fee (%)
          </label>
          <input
            type="number"
            value={inputs.currentProcessingFee}
            onChange={(e) => setInputs({ ...inputs, currentProcessingFee: parseFloat(e.target.value) || 0 })}
            className="w-full px-4 py-2 rounded-lg bg-[#111010] text-[#EBFFEE] border border-[#504A4A] focus:border-[#9B1421] focus:outline-none"
            min="0"
            max="10"
            step="0.1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Current Members
          </label>
          <input
            type="number"
            value={inputs.memberCount}
            onChange={(e) => setInputs({ ...inputs, memberCount: parseInt(e.target.value) || 0 })}
            className="w-full px-4 py-2 rounded-lg bg-[#111010] text-[#EBFFEE] border border-[#504A4A] focus:border-[#9B1421] focus:outline-none"
            min="1"
            step="1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Avg Membership Price ($)
          </label>
          <input
            type="number"
            value={inputs.avgMembershipPrice}
            onChange={(e) => setInputs({ ...inputs, avgMembershipPrice: parseFloat(e.target.value) || 0 })}
            className="w-full px-4 py-2 rounded-lg bg-[#111010] text-[#EBFFEE] border border-[#504A4A] focus:border-[#9B1421] focus:outline-none"
            min="0"
            step="1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Monthly Growth Rate (%)
            <span className="text-xs text-[#A9A3A3] ml-2">(Suggested: 2-5%)</span>
          </label>
          <input
            type="number"
            value={inputs.monthlyGrowthRate}
            onChange={(e) => setInputs({ ...inputs, monthlyGrowthRate: parseFloat(e.target.value) || 0 })}
            className="w-full px-4 py-2 rounded-lg bg-[#111010] text-[#EBFFEE] border border-[#504A4A] focus:border-[#9B1421] focus:outline-none"
            min="0"
            max="20"
            step="0.1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
            Retention Rate (%)
            <span className="text-xs text-[#A9A3A3] ml-2">(Suggested: 85-95%)</span>
          </label>
          <input
            type="number"
            value={inputs.retentionRate}
            onChange={(e) => setInputs({ ...inputs, retentionRate: parseFloat(e.target.value) || 0 })}
            className="w-full px-4 py-2 rounded-lg bg-[#111010] text-[#EBFFEE] border border-[#504A4A] focus:border-[#9B1421] focus:outline-none"
            min="0"
            max="100"
            step="1"
          />
        </div>
      </div>

      {/* Results Summary */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-[#111010] rounded-lg p-4 border border-[#504A4A]">
          <div className="text-sm text-[#A9A3A3] mb-1">Immediate Savings</div>
          <div className="text-2xl font-bold text-rollops-accent">${immediateSavings.toFixed(0)}/mo</div>
          <div className="text-xs text-[#706A6A] mt-1">SaaS cost reduction</div>
        </div>
        <div className="bg-[#111010] rounded-lg p-4 border border-[#504A4A]">
          <div className="text-sm text-[#A9A3A3] mb-1">Month 12 Savings</div>
          <div className="text-2xl font-bold text-[#EBFFEE]">${month12Savings.toFixed(0)}/mo</div>
          <div className="text-xs text-[#706A6A] mt-1">After full transition</div>
        </div>
        <div className="bg-[#111010] rounded-lg p-4 border border-[#504A4A]">
          <div className="text-sm text-[#A9A3A3] mb-1">Year 1 Total Savings</div>
          <div className="text-2xl font-bold text-[#EBFFEE]">${year1TotalSavings.toFixed(0)}</div>
          <div className="text-xs text-[#706A6A] mt-1">Cumulative savings</div>
        </div>
        <div className="bg-[#111010] rounded-lg p-4 border border-[#504A4A]">
          <div className="text-sm text-[#A9A3A3] mb-1">Projected Members</div>
          <div className="text-2xl font-bold text-[#EBFFEE]">{projectedMembers}</div>
          <div className="text-xs text-[#706A6A] mt-1">By month 12</div>
        </div>
      </div>

      {/* Monthly Breakdown */}
      <div className="bg-[#111010] rounded-lg p-4 border border-[#504A4A] mb-4">
        <h4 className="text-lg font-bold mb-4">12-Month Transition Timeline</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#504A4A]">
                <th className="text-left py-2 text-[#A9A3A3]">Month</th>
                <th className="text-right py-2 text-[#A9A3A3]">Members</th>
                <th className="text-right py-2 text-[#A9A3A3]">On Zero Fee</th>
                <th className="text-right py-2 text-[#A9A3A3]">Monthly Cost</th>
                <th className="text-right py-2 text-[#A9A3A3]">Monthly Savings</th>
                <th className="text-right py-2 text-[#A9A3A3]">Cumulative</th>
              </tr>
            </thead>
            <tbody>
              {[0, 2, 5, 8, 11].map((idx) => {
                const data = transitionData[idx];
                return (
                  <tr key={data.month} className="border-b border-[#504A4A]/50">
                    <td className="py-2 text-[#EBFFEE] font-semibold">Month {data.month}</td>
                    <td className="py-2 text-right text-[#EBFFEE]">{data.members}</td>
                    <td className="py-2 text-right text-[#EBFFEE]">{data.membersOnZeroFee}</td>
                    <td className="py-2 text-right text-[#A9A3A3]">${data.rollOpsTotalCost.toFixed(0)}</td>
                    <td className="py-2 text-right text-rollops-accent font-bold">${data.monthlySavings.toFixed(0)}</td>
                    <td className="py-2 text-right text-[#EBFFEE] font-bold">${data.cumulativeSavings.toFixed(0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-[#1A1818] rounded p-3 border border-[#504A4A]">
        <p className="text-xs text-[#706A6A] text-center">
          <strong className="text-[#A9A3A3]">Note:</strong> All calculations are estimates. Actual savings depend on your transition plan, member growth, and retention. 
          Zero Fee transition options allow you to control the pace - new students start immediately, existing students transition at contract renewal or on your timeline.
        </p>
      </div>
    </div>
  );
}

export default function FoundersPromo() {
  const navigate = useNavigate();
  const [_showFAQ, _setShowFAQ] = useState(false);
  const { data: promoCount = 0, isLoading: isLoadingCount } = useFoundersPromoCount();
  const display = getFoundersPromoDisplay(promoCount);
  const remainingSpots = Math.max(0, 100 - promoCount);
  const hasTrackedVisit = useRef(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  // Track page visit on mount
  useEffect(() => {
    if (!hasTrackedVisit.current) {
      trackFoundersPromoPageVisit();
      hasTrackedVisit.current = true;
    }

    // Track abandon on page unload
    const handleBeforeUnload = () => {
      trackFoundersPromoAbandon();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      // Also track abandon when component unmounts (navigation away)
      if (hasTrackedVisit.current) {
        trackFoundersPromoAbandon();
      }
    };
  }, []);

  // Fetch video URL from Supabase storage
  useEffect(() => {
    async function fetchVideoUrl() {
      try {
        // Video path in the promo-videos bucket
        // Upload videos to: promo-videos/{PROMO_VIDEO_FILENAME}
        const { data: urlData } = supabase.storage
          .from('promo-videos')
          .getPublicUrl(PROMO_VIDEO_FILENAME);

        if (urlData?.publicUrl) {
          // Verify the video exists by checking if we can access it
          const response = await fetch(urlData.publicUrl, { method: 'HEAD' });
          if (response.ok) {
            setVideoUrl(urlData.publicUrl);
          } else {
            devLog('[FoundersPromo] Video not found at path:', PROMO_VIDEO_FILENAME);
          }
        }
      } catch (error) {
        console.error('[FoundersPromo] Error fetching video URL:', error);
      }
    }

    fetchVideoUrl();
  }, []);

  const handleCTAClick = (location: string) => {
    trackFoundersPromoCTAClick(location);
    navigate('/owner-signup');
  };

  return (
    <>
      <Helmet>
        <title>Founders Promo - RollOps | $20/month for First Year</title>
        <meta name="description" content="Join the Founders Promo: Get RollOps Plus for just $20/month for your first year. Zero credit card processing fees. Limited to first 100 gyms." />
      </Helmet>

      <main className="pt-16 min-h-screen bg-[#111010] text-[#EBFFEE] font-['Inter']">
        {/* Hero Section - Squeeze Page Style */}
        <section className="bg-gradient-to-br from-[#9B1421]/30 via-[#111010] to-[#111010] py-12 md:py-20 px-4">
          <div className="container mx-auto max-w-5xl">
            {/* Urgency Badge */}
            {display.urgencyText && (
              <div className="text-center mb-6">
                <div className="inline-block bg-[#9B1421]/20 border-2 border-[#9B1421] rounded-full px-6 py-2">
                  <span className="text-[#EBFFEE] font-bold text-sm md:text-base">
                    ⚡ LIMITED TIME: {display.urgencyText}
                  </span>
                </div>
              </div>
            )}

            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-6 leading-tight">
              <span className="text-[#EBFFEE]">Stop Losing Money on</span>{' '}
              <span className="text-[#EBFFEE]">Credit Card Fees</span>
            </h1>
            
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8 text-[#EBFFEE]">
              Get RollOps Plus for Just <span className="text-[#EBFFEE]">$20/Month</span> Your First Year
            </h2>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-[#A9A3A3] text-center mb-8 max-w-3xl mx-auto leading-relaxed">
              Join the first 100 gym owners who get <strong className="text-[#EBFFEE]">all RollOps Plus features</strong> at a fraction of the cost, 
              plus <strong className="text-[#EBFFEE]">zero credit card processing fees</strong> forever.
            </p>

            {/* Promo Video */}
            {videoUrl && (
              <div className="mb-10 max-w-4xl mx-auto">
                <div className="bg-[#221F1F] rounded-xl border-2 border-[#9B1421]/50 p-4 md:p-6">
                  <div className="aspect-video w-full rounded-lg overflow-hidden bg-[#111010]">
                    <video
                      src={videoUrl}
                      controls
                      className="w-full h-full object-contain"
                      preload="metadata"
                      playsInline
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </div>
              </div>
            )}

            {/* Social Proof Counter */}
            <div className="mb-10 max-w-2xl mx-auto">
              <TwinklingGoldBorder className="rounded-2xl bg-[#111010]">
                <div className="p-6 md:p-8 text-center">
                  {display.showCounter ? (
                    <div className="text-5xl md:text-6xl font-bold text-[#EBFFEE] mb-2">
                      {isLoadingCount ? '...' : `${promoCount}/100`}
                    </div>
                  ) : (
                    <div className="text-3xl md:text-4xl font-bold text-[#EBFFEE] mb-2">
                      {isLoadingCount ? '...' : display.mainText}
                    </div>
                  )}
                  {display.subText && (
                    <p className="text-lg md:text-xl text-[#A9A3A3] mb-4">
                      {display.subText}
                    </p>
                  )}
                  {display.showRemaining && remainingSpots > 0 && (
                    <p className="text-sm text-[#EBFFEE] font-semibold">
                      {display.urgencyText || `${remainingSpots} spots remaining at this price`}
                    </p>
                  )}
                </div>
              </TwinklingGoldBorder>
            </div>

            {/* Primary CTA */}
            <div className="text-center mb-12">
              <button
                onClick={() => handleCTAClick('hero-primary')}
                className="inline-block bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-8 md:px-12 py-4 md:py-5 rounded-lg font-bold text-lg md:text-xl transition-all transform hover:scale-105 shadow-lg shadow-[#9B1421]/50"
              >
                Claim Your Spot Now - $20/Month
                <ArrowRight className="inline-block ml-2 w-5 h-5" />
              </button>
              <p className="text-sm text-[#A9A3A3] mt-4">
                No credit card required • Start your 30-day free trial today
              </p>
            </div>
          </div>
        </section>

        {/* What You Get Section */}
        <section className="py-12 md:py-20 px-4 bg-[#1A1818]">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              What You Get With <span className="text-[#EBFFEE]">RollOps Plus</span>
            </h2>
            <p className="text-center text-[#A9A3A3] mb-12 text-lg">
              Everything you need to run your gym, all included in your Founders Promo membership
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-12">
              {/* Zero Fee Processing */}
              <div className="bg-[#221F1F] p-6 rounded-lg border border-[#504A4A] hover:border-[#9B1421] transition-colors">
                <div className="flex items-start gap-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <DollarSign className="w-6 h-6 text-text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Zero Credit Card Processing Fees</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      You get <strong className="text-[#EBFFEE]">100% of your subscription price</strong> - zero processing fees. 
                      <strong className="text-[#EBFFEE]"> $100 membership = $100 in the bank.</strong> Legally and safely pass credit card fees onto end customers without abruptly changing their bill. Terms and conditions apply.
                    </p>
                  </div>
                </div>
              </div>

              {/* Digital Signup Flow */}
              <div className="bg-[#221F1F] p-6 rounded-lg border border-[#504A4A] hover:border-[#9B1421] transition-colors">
                <div className="flex items-start gap-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <Smartphone className="w-6 h-6 text-text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Complete Digital Signup Flow</h3>
                    <p className="text-[#A9A3A3] leading-relaxed mb-2">
                      Everything digital and mobile-first. No more paper, no more manual entry.
                    </p>
                    <ul className="text-[#A9A3A3] list-disc list-inside space-y-1">
                      <li><strong className="text-[#EBFFEE]">Authorizations</strong></li>
                      <li><strong className="text-[#EBFFEE]">Registration</strong></li>
                      <li><strong className="text-[#EBFFEE]">Subscription Enrollment</strong></li>
                      <li><strong className="text-[#EBFFEE]">Class Signup</strong></li>
                      <li><strong className="text-[#EBFFEE]">Attendance</strong></li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Member Management */}
              <div className="bg-[#221F1F] p-6 rounded-lg border border-[#504A4A] hover:border-[#9B1421] transition-colors">
                <div className="flex items-start gap-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <Users className="w-6 h-6 text-text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Complete Member Management</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      Manage members, track attendance, handle enrollments, and store digital waivers - all in one place, 
                      accessible from your phone or computer.
                    </p>
                  </div>
                </div>
              </div>

              {/* Automated Billing */}
              <div className="bg-[#221F1F] p-6 rounded-lg border border-[#504A4A] hover:border-[#9B1421] transition-colors">
                <div className="flex items-start gap-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <CreditCard className="w-6 h-6 text-text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Automated Subscription Billing</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      Set up recurring subscriptions once, and RollOps handles the rest. Members are automatically charged, 
                      and you get paid - no manual processing needed.
                    </p>
                  </div>
                </div>
              </div>

              {/* Class Scheduling */}
              <div className="bg-[#221F1F] p-6 rounded-lg border border-[#504A4A] hover:border-[#9B1421] transition-colors">
                <div className="flex items-start gap-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <Calendar className="w-6 h-6 text-text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Class Scheduling & Attendance</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      Create schedules, manage enrollments, and track attendance automatically with geofencing or manual check-in. 
                      Keep your members engaged and coming back.
                    </p>
                  </div>
                </div>
              </div>

              {/* Mobile First */}
              <div className="bg-[#221F1F] p-6 rounded-lg border border-[#504A4A] hover:border-[#9B1421] transition-colors">
                <div className="flex items-start gap-4">
                  <div className="bg-[#9B1421]/20 p-3 rounded-lg flex-shrink-0">
                    <Zap className="w-6 h-6 text-text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Mobile-First Design</h3>
                    <p className="text-[#A9A3A3] leading-relaxed">
                      Manage your gym on the go with our iOS and Android apps. Everything works seamlessly across devices - 
                      your phone, tablet, or computer.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap justify-center gap-6 md:gap-8 mb-12">
              <div className="flex items-center gap-2 text-[#A9A3A3]">
                <Shield className="w-5 h-5 text-rollops-accent" />
                <span className="text-sm">Bank-Level Security</span>
              </div>
              <div className="flex items-center gap-2 text-[#A9A3A3]">
                <Clock className="w-5 h-5 text-rollops-accent" />
                <span className="text-sm">30-Day Free Trial</span>
              </div>
              <div className="flex items-center gap-2 text-[#A9A3A3]">
                <CheckCircle className="w-5 h-5 text-rollops-accent" />
                <span className="text-sm">No Credit Card Required</span>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Comparison */}
        <section className="py-12 md:py-20 px-4">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              The Math That <span className="text-[#EBFFEE]">Makes Sense</span>
            </h2>
            
            <div className="bg-[#221F1F] border-2 border-[#9B1421]/50 rounded-2xl p-8 md:p-12">
              <TransitionCalculator />
              
              <div className="bg-[#1A1818] rounded-lg p-6 mb-6 mt-8">
                <h3 className="text-xl font-bold mb-2 text-center">What You&apos;re Paying vs. What You&apos;ll Pay</h3>
                <p className="text-sm text-[#A9A3A3] text-center mb-6">
                  <em>Use the calculator above to see your personalized projections</em>
                </p>
                
                {/* Simple Before/After Comparison */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Before */}
                  <div className="bg-[#111010] rounded-lg p-6 border-2 border-[#9B1421]/30">
                    <h4 className="text-lg font-bold text-rollops-accent mb-4 text-center">What You&apos;re Paying Now</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-[#A9A3A3]">SaaS Platform:</span>
                        <span className="text-[#EBFFEE] font-bold text-lg">$100/month</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[#A9A3A3]">Processing Fees (4%):</span>
                        <span className="text-rollops-accent font-bold text-lg">$200/month</span>
                      </div>
                      <div className="border-t-2 border-[#504A4A] pt-3 mt-3">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-semibold text-[#EBFFEE]">Total Monthly Cost:</span>
                          <span className="text-2xl font-bold text-rollops-accent">$300</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* After - Full Transition */}
                  <div className="bg-[#111010] rounded-lg p-6 border-2 border-[#9B1421]">
                    <h4 className="text-lg font-bold text-rollops-accent mb-4 text-center">What You&apos;ll Pay with RollOps</h4>
                    <div className="space-y-3 mb-3">
                      <div className="flex justify-between items-center">
                        <span className="text-[#A9A3A3]">RollOps Platform:</span>
                        <span className="text-[#EBFFEE] font-bold text-lg">$20/month</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[#A9A3A3]">Processing Fees:</span>
                        <span className="text-[#EBFFEE] font-bold text-lg">$0/month</span>
                      </div>
                      <div className="border-t-2 border-[#504A4A] pt-3 mt-3">
                        <div className="flex justify-between items-center">
                          <span className="text-lg font-semibold text-[#EBFFEE]">Total Monthly Cost:</span>
                          <span className="text-2xl font-bold text-[#EBFFEE]">$20</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-[#1A1818] rounded p-3 border border-[#504A4A]">
                      <p className="text-xs text-[#A9A3A3] text-center">
                        <strong className="text-[#EBFFEE]">After full transition</strong> - You control the pace. New students start immediately, existing students transition at contract renewal or on your timeline.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Immediate Savings */}
                <div className="bg-[#111010] rounded-lg p-6 border border-[#504A4A] mb-6">
                  <h4 className="text-lg font-bold text-center mb-4">Your Savings</h4>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="text-center">
                      <div className="text-2xl md:text-3xl font-bold text-[#EBFFEE] mb-2">
                        <span className="text-rollops-accent">$80/month</span> saved immediately
                      </div>
                      <p className="text-sm text-[#A9A3A3]">
                        SaaS cost drops from $100 to $20 when you switch
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl md:text-3xl font-bold text-[#EBFFEE] mb-2">
                        <span className="text-rollops-accent">$280/month</span> saved after transition
                      </div>
                      <p className="text-sm text-[#A9A3A3]">
                        Once all students are on Zero Fee billing
                      </p>
                    </div>
                  </div>
                </div>

                {/* Transition Timeline */}
                <div className="bg-[#111010] rounded-lg p-6 border border-[#504A4A] mb-6">
                  <h4 className="text-lg font-bold text-center mb-4">How the Transition Works</h4>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="bg-[#9B1421]/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-rollops-accent font-bold text-sm">1</span>
                      </div>
                      <div>
                        <h5 className="font-semibold text-[#EBFFEE] mb-1">Immediate Savings: Switch to RollOps</h5>
                        <p className="text-sm text-[#A9A3A3] mb-2">
                          Your SaaS cost drops from $100 to $20 immediately - that&apos;s <strong className="text-[#EBFFEE]">$80/month saved right away</strong>. No need to wait.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="bg-[#9B1421]/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-text-primary font-bold text-sm">2</span>
                      </div>
                      <div>
                        <h5 className="font-semibold text-[#EBFFEE] mb-1">You Control the Transition Pace</h5>
                        <p className="text-sm text-[#A9A3A3] mb-2">
                          <strong className="text-[#EBFFEE]">New students:</strong> Start on Zero Fee billing immediately - no price increase needed.
                        </p>
                        <p className="text-sm text-[#A9A3A3] mb-2">
                          <strong className="text-[#EBFFEE]">Existing students:</strong> Transition at contract renewal, or set your own timeline. For month-to-month students, you decide when to switch - with ample time to provide notices.
                        </p>
                        <p className="text-sm text-[#A9A3A3]">
                          <strong className="text-[#EBFFEE]">Gradual rollout:</strong> Option to stage transitions over 6 months to avoid sticker shock. No sudden price increases for existing students.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="bg-[#9B1421]/20 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-rollops-accent font-bold text-sm">3</span>
                      </div>
                      <div>
                        <h5 className="font-semibold text-[#EBFFEE] mb-1">Full Zero Fee: Maximum Savings</h5>
                        <p className="text-sm text-[#A9A3A3]">
                          Once all students are transitioned to Zero Fee billing, you&apos;re paying just $20/month for RollOps with zero processing fees. That&apos;s <strong className="text-[#EBFFEE]">$280/month saved</strong> compared to what you&apos;re paying now.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Key Numbers */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t border-[#504A4A]">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#EBFFEE] mb-1">$280</div>
                    <div className="text-xs text-[#A9A3A3]">Saved Per Month</div>
                    <div className="text-xs text-[#706A6A] mt-1">(By Month 12)</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-[#EBFFEE] mb-1">$3,360</div>
                    <div className="text-xs text-[#A9A3A3]">Saved Per Year</div>
                    <div className="text-xs text-[#706A6A] mt-1">(After Month 12)</div>
                  </div>
                  <div className="text-center col-span-2 md:col-span-1">
                    <div className="text-2xl font-bold text-[#EBFFEE] mb-1">$2,060+</div>
                    <div className="text-xs text-[#A9A3A3]">Year 1 Total Savings</div>
                    <div className="text-xs text-[#706A6A] mt-1">(During transition)</div>
                  </div>
                </div>

                <div className="text-center pt-4 border-t border-[#504A4A] mt-4">
                  <p className="text-xs text-[#706A6A]">
                    Zero Fee has transition options. <Link to="/features/billing" className="text-rollops-accent hover:text-[#EBFFEE] underline">Learn more</Link> about Zero Fee options.
                  </p>
                </div>
              </div>

              <p className="text-center text-[#A9A3A3] text-sm">
                <strong className="text-[#EBFFEE]">Plus</strong> you get all RollOps Plus features for just $20/month your first year. 
                After year one, standard pricing applies (you&apos;ll still save on processing fees forever).
              </p>
            </div>
          </div>
        </section>

        {/* Book a Demo Section */}
        <section className="py-12 md:py-20 px-4 bg-gradient-to-br from-[#9B1421]/20 to-[#111010]">
          <div className="container mx-auto max-w-4xl">
            <div className="bg-[#221F1F] border-2 border-[#9B1421] rounded-2xl p-8 md:p-12 text-center">
              <div className="inline-block bg-[#9B1421]/20 p-4 rounded-full mb-6">
                <Video className="w-10 h-10 text-text-primary" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Still Have Questions?
              </h2>
              <p className="text-xl text-[#A9A3A3] mb-2">
                Book a 15-30 minute demo with the Founder/CEO
              </p>
              <p className="text-sm text-[#706A6A] mb-8 max-w-2xl mx-auto">
                Limited time offer - Get on the phone immediately and see how RollOps can transform your gym. 
                We&apos;ll answer all your questions and get you ready to start saving money.
              </p>
              {GOOGLE_CALENDAR_BOOKING_URL ? (
                <>
                  <a
                    href={GOOGLE_CALENDAR_BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-8 py-4 rounded-lg font-semibold text-lg transition-colors mb-4"
                  >
                    Book a Demo Now
                  </a>
                  <p className="text-xs text-[#706A6A]">
                    Opens Google Calendar booking - Select your preferred time slot
                  </p>
                </>
              ) : (
                <>
                  <a
                    href="mailto:support@rollops.pro?subject=Demo Request - Founders Promo"
                    className="inline-block bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-8 py-4 rounded-lg font-semibold text-lg transition-colors mb-4"
                  >
                    Request a Demo
                  </a>
                  <p className="text-xs text-[#706A6A]">
                    Email us to schedule a 15-30 minute demo with the Founder/CEO
                  </p>
                </>
              )}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-12 md:py-20 px-4 bg-[#1A1818]">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>

            <div className="space-y-4">
              <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-6">
                <h3 className="text-xl font-bold mb-2">Do I need a credit card to start?</h3>
                <p className="text-[#A9A3A3]">
                  No! You can start your 30-day free trial without a credit card. However, to enable subscription payments 
                  and process member payments, you&apos;ll need to set up billing after your trial period.
                </p>
              </div>

              <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-6">
                <h3 className="text-xl font-bold mb-2">What happens after the first year?</h3>
                <p className="text-[#A9A3A3]">
                  After your first year at $20/month, standard RollOps Plus pricing applies. But you&apos;ll still have 
                  <strong className="text-[#EBFFEE]"> zero credit card processing fees forever</strong> - that never changes.
                </p>
              </div>

              <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-6">
                <h3 className="text-xl font-bold mb-2">What are the processing fees?</h3>
                <p className="text-[#A9A3A3]">
                  RollOps charges <strong className="text-[#EBFFEE]">zero processing fees to gym owners</strong>. 
                  You get <strong className="text-[#EBFFEE]">100% of your subscription price</strong> - zero processing fees. 
                  <strong className="text-[#EBFFEE]"> $100 membership = $100 in the bank.</strong> Legally and safely pass credit card fees onto end customers without abruptly changing their bill. Terms and conditions apply.
                </p>
              </div>

              <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-6">
                <h3 className="text-xl font-bold mb-2">Can I cancel anytime?</h3>
                <p className="text-[#A9A3A3]">
                  Yes! You can cancel your subscription at any time. There are no long-term contracts or cancellation fees. 
                  You&apos;ll retain access until the end of your current billing period.
                </p>
              </div>

              <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg p-6">
                <h3 className="text-xl font-bold mb-2">What if I have questions before signing up?</h3>
                <p className="text-[#A9A3A3]">
                  We&apos;re here to help! Contact us at{' '}
                  <a href="mailto:support@rollops.pro" className="text-[#EBFFEE] hover:text-[#A9A3A3] underline">
                    support@rollops.pro
                  </a>{' '}
                  or call{' '}
                  <a href="tel:17576556779" className="text-[#EBFFEE] hover:text-[#A9A3A3] underline">
                    (757) 655-6779
                  </a>. 
                  We&apos;d love to answer any questions you have.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-12 md:py-20 px-4 bg-gradient-to-br from-[#9B1421]/20 to-[#111010]">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Stop Losing Money on Processing Fees?
            </h2>
            <p className="text-xl text-[#A9A3A3] mb-8">
              Join the first 100 gym owners and lock in $20/month for your first year, plus zero processing fees forever.
            </p>
            
            {remainingSpots > 0 ? (
              <>
                <div className="bg-[#221F1F] border-2 border-[#9B1421] rounded-lg p-6 mb-8 inline-block">
                  <div className="text-4xl font-bold text-[#EBFFEE] mb-2">
                    {display.showRemaining ? `${remainingSpots} Spots Left` : 'Limited Spots Remaining'}
                  </div>
                  <div className="text-sm text-[#A9A3A3]">
                    Don&apos;t miss out on this limited-time offer
                  </div>
                </div>
                
                <button
                  onClick={() => handleCTAClick('final-cta')}
                  className="inline-block bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-10 md:px-12 py-5 md:py-6 rounded-lg font-bold text-xl md:text-2xl transition-all transform hover:scale-105 shadow-lg shadow-[#9B1421]/50 mb-4"
                >
                  Claim Your Spot Now
                  <ArrowRight className="inline-block ml-2 w-6 h-6" />
                </button>
                <p className="text-sm text-[#A9A3A3]">
                  No credit card required • 30-day free trial • Cancel anytime
                </p>
              </>
            ) : (
              <div className="bg-[#221F1F] border-2 border-[#504A4A] rounded-lg p-8">
                <p className="text-xl text-[#A9A3A3] mb-4">
                  The Founders Promo has sold out! But you can still get RollOps Plus with zero processing fees.
                </p>
                <button
                  onClick={() => handleCTAClick('sold-out-cta')}
                  className="inline-block bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE] px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
                >
                  Sign Up Now
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 px-4 bg-[#1A1818] border-t border-[#504A4A]">
          <div className="container mx-auto max-w-4xl">
            <p className="text-xs text-[#706A6A] text-center leading-relaxed">
              <strong className="text-[#A9A3A3]">Important:</strong> Enabling subscription payments and processing member payments requires 
              an active paid subscription. You can start your 30-day free trial without a credit card, but to process payments, 
              you&apos;ll need to set up billing after your trial period. With RollOps Zero Fee billing, you receive 100% of your subscription price 
              with zero processing fees deducted from your revenue. 
              <Link to="/terms" className="text-[#EBFFEE] hover:text-[#A9A3A3] underline ml-1">
                See full terms
              </Link>.
            </p>
          </div>
        </section>
      </main>

    </>
  );
}
